import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KpiValue } from '../../statistics-data.service';

@Component({
  standalone: true,
  selector: 'app-kpi-card',
  templateUrl: './kpi-card.component.html',
  styleUrls: ['./kpi-card.component.scss'],
  imports: [CommonModule, DecimalPipe, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCardComponent {
  @Input() title: string = '';
  @Input() value: number = 0;
  @Input() previousValue: number | null = null;
  @Input() trend: number | null = null; // -1 to 1 as decimal
  @Input() format: 'currency' | 'number' | 'percent' = 'number';
  @Input() currencyCode: string = 'EUR';
  @Input() icon: string = 'pi-chart-line';
  @Input() iconColor: 'primary' | 'success' | 'warning' | 'danger' | 'info' =
    'primary';
  @Input() loading: boolean = false;
  @Input() compact: boolean = false;

  // Trend is considered "up-good" by default (revenue, sales)
  // Set to false for metrics where down is good (churn, no-shows)
  @Input() upIsGood: boolean = true;

  constructor(private translate: TranslateService) {}

  get trendDirection(): 'up' | 'down' | 'neutral' {
    if (this.trend === null || Math.abs(this.trend) < 0.005) return 'neutral';
    return this.trend > 0 ? 'up' : 'down';
  }

  get trendValue(): number {
    return Math.abs((this.trend || 0) * 100);
  }

  get trendClass(): string {
    if (this.trendDirection === 'neutral') return 'neutral';
    const isPositive = this.trendDirection === 'up';
    const isGood = this.upIsGood ? isPositive : !isPositive;
    return isGood ? 'positive' : 'negative';
  }

  get trendIcon(): string {
    switch (this.trendDirection) {
      case 'up':
        return 'pi-arrow-up';
      case 'down':
        return 'pi-arrow-down';
      default:
        return 'pi-minus';
    }
  }

  get iconColorClass(): string {
    return `icon--${this.iconColor}`;
  }

  get formattedValue(): string {
    if (this.loading) return '---';

    switch (this.format) {
      case 'currency':
        return new Intl.NumberFormat(this.getLocale(), {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(this.value);
      case 'percent':
        return `${this.value.toFixed(1)}%`;
      default:
        return new Intl.NumberFormat(this.getLocale()).format(this.value);
    }
  }

  private getLocale(): string {
    const lang = (this.translate.currentLang || 'es').toLowerCase();
    if (lang.startsWith('en')) return 'en-US';
    if (lang.startsWith('ca')) return 'ca-ES';
    return 'es-ES';
  }
}
